import { useEffect, useMemo, useRef } from "react";
import type { SessionRecordingSummary, VideoFile } from "@/types";

/**
 * Deep link from the session detail recording cards (issue #1000):
 * ?recording=<recordingId> selects that recording's first completed chapter.
 * The returned `requestedVideoId` is null until the recording (and a playable
 * chapter) has loaded — the player applies the id one-shot once it becomes
 * selectable. The link applies once per session: layout swaps across the lg
 * breakpoint remount the player (resetting its one-shot guard), so forwarding
 * stops once the selection has landed on it.
 */
export function useRecordingDeepLink(
  sessionId: string | undefined,
  recordings: SessionRecordingSummary[],
  availableVideos: VideoFile[],
  selectedVideoId: string | null,
  searchParams: URLSearchParams,
): string | undefined {
  // Applied guard: the last deep-linked video id already landed on the
  // player. Reset per session during render (mirroring the page's
  // per-session reset block) so a stale id never suppresses a fresh link.
  const appliedRef = useRef<string | undefined>(undefined);
  const lastSessionRef = useRef<string | undefined>(undefined);
  if (lastSessionRef.current !== sessionId) {
    lastSessionRef.current = sessionId;
    appliedRef.current = undefined;
  }

  const deepLinkedVideoId = useMemo(() => {
    const recordingId = searchParams.get("recording");
    if (!recordingId) {
      return null;
    }
    const recording = recordings.find((item) => item.id === recordingId);
    if (!recording) {
      return null;
    }
    const availableVideoIds = new Set(
      availableVideos.map((video) => video.id),
    );
    const chapter = recording.chapters.find((candidate) =>
      availableVideoIds.has(candidate.id),
    );
    return chapter?.id ?? null;
  }, [recordings, availableVideos, searchParams]);

  // Mark the recording deep link as applied once the selection lands on it,
  // so later layout swaps never revert a manual selection.
  useEffect(() => {
    if (deepLinkedVideoId && selectedVideoId === deepLinkedVideoId) {
      appliedRef.current = deepLinkedVideoId;
    }
  }, [deepLinkedVideoId, selectedVideoId]);

  return deepLinkedVideoId && appliedRef.current !== deepLinkedVideoId
    ? deepLinkedVideoId
    : undefined;
}
